
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChatbotData } from '@/components/chat/chatTypes';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface PersonaListProps {
  chatbots: ChatbotData[];
  onPersonaDeleted: (id: string) => void;
}

const PersonaList = ({ chatbots, onPersonaDeleted }: PersonaListProps) => {
  const { toast } = useToast();
  
  // Delete a persona
  const handleDeletePersona = async (id: string) => {
    try {
      // Delete agent (will cascade to system_prompts due to foreign key)
      const { error } = await supabase
        .from('agents')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Notify parent component
      onPersonaDeleted(id);
      
      toast({
        title: "Persona Deleted",
        description: "The chatbot persona has been removed"
      });
    } catch (error) {
      console.error('Error deleting persona:', error);
      toast({
        title: "Error",
        description: "Failed to delete persona",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manage Existing Personas</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chatbots.length > 0 ? (
              chatbots.map(chatbot => (
                <TableRow key={chatbot.id}>
                  <TableCell className="font-medium">{chatbot.name}</TableCell>
                  <TableCell className="max-w-xs truncate">{chatbot.description}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {chatbot.tags.map(tag => (
                        <Badge key={tag} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDeletePersona(chatbot.id)}
                      >
                        <Trash2 size={14} />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                  No personas found. Add your first one above.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default PersonaList;
